// src/pages/VerifyEmail.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { verifyEmail, resendVerificationCode } from '../utils/api';
import './VerifyEmail.css';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize theme
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const darkMode = savedTheme === 'dark';
    setIsDarkMode(darkMode);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Check for pending Google user on mount and get email
  useEffect(() => {
    const pendingUser = localStorage.getItem('pendingGoogleUser');
    if (pendingUser) {
      try {
        const parsed = JSON.parse(pendingUser);
        setPendingGoogleUser(parsed);
        if (parsed.user?.email) {
          setUserEmail(parsed.user.email);
        }
      } catch (error) {
        console.error('Error parsing pending Google user:', error);
      }
    }
    
    // Get email from location state (if redirected from signup)
    if (location.state?.email) {
      setUserEmail(location.state.email);
    }
  }, [location]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    const theme = newDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setCode(value);
      setMessage(''); // Clear message when user types
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!code || code.length !== 6) {
      setMessage('Please enter a valid 6-digit verification code');
      return;
    }

    setIsLoading(true);
    setMessage('');
    
    try {
      const response = await verifyEmail(code);
      if (response.success) {
        setMessage('Email verified successfully! Redirecting...');
        
        // Check if this is a Google Sign-In user
        const pendingUser = pendingGoogleUser || (localStorage.getItem('pendingGoogleUser') ? JSON.parse(localStorage.getItem('pendingGoogleUser')) : null);
        
        if (pendingUser) {
          // Clear pending user data
          localStorage.removeItem('pendingGoogleUser');
          
          // If role selection is needed, redirect to signup with role selection
          if (pendingUser.needsRoleSelection) {
            setTimeout(() => {
              navigate('/signup', { 
                state: { 
                  tempUserId: pendingUser.tempUserId,
                  fromGoogle: true,
                  user: pendingUser.user
                } 
              });
            }, 1500);
          } else {
            // Otherwise, redirect to login
            setTimeout(() => {
              navigate('/login');
            }, 1500);
          }
        } else {
          // Regular email verification - redirect to login
          setTimeout(() => {
            navigate('/login');
          }, 1500);
        }
      } else {
        setMessage(response.message || 'Invalid or expired verification code');
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'Error verifying email. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!userEmail) {
      setMessage('Email address not found. Please sign up again.');
      return;
    }

    if (resendCooldown > 0) {
      return;
    }

    setIsResending(true);
    setMessage('');
    
    try {
      const response = await resendVerificationCode(userEmail);
      if (response.success) {
        setMessage('Verification code has been resent to your email');
        setResendCooldown(30); // 30 seconds cooldown
      } else {
        setMessage(response.message || 'Failed to resend verification code');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error resending verification code. Please try again later.';
      setMessage(errorMessage);
      
      // Handle rate limiting
      if (error.response?.status === 429 && error.response?.data?.retryAfter) {
        setResendCooldown(error.response.data.retryAfter);
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="verify-email-page">
      {/* Dark Mode Toggle Button */}
      <button
        onClick={toggleDarkMode}
        className="theme-toggle-btn"
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDarkMode ? (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      {/* Background Gradient Overlay */}
      <div className="verify-email-overlay"></div>

      {/* Main Container */}
      <div className="verify-email-container">
        {/* Header Section */}
        <div className="verify-email-header">
          <Link to="/" className="verify-email-logo">
            <span className="logo-text">WEHEAL</span>
          </Link>
          <div className="verify-email-subtitle">
            Email Verification
          </div>
        </div>

        {/* Card */}
        <div className="verify-email-card">
          {/* Icon */}
          <div className="verify-email-icon">
            <svg className="icon-envelope" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>

          {/* Title */}
          <h1 className="verify-email-title">Verify Your Email</h1>
          
          {/* Description */}
          <p className="verify-email-description">
            {pendingGoogleUser 
              ? 'Enter the 6-digit verification code sent to your email to complete your Google Sign-In'
              : 'We\'ve sent a 6-digit verification code to your email address. Please enter it below to verify your account.'}
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="verify-email-form">
            {/* OTP Input */}
            <div className="otp-input-wrapper">
              <label htmlFor="code" className="otp-label">Verification Code</label>
              <div className="otp-input-container">
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  required
                  maxLength="6"
                  className="otp-input"
                  disabled={isLoading}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                />
                <div className="otp-input-underline">
                  {code.split('').map((digit, index) => (
                    <span key={index} className={`otp-digit ${digit ? 'filled' : ''}`}>
                      {digit || '○'}
                    </span>
                  ))}
                  {code.length < 6 && Array.from({ length: 6 - code.length }).map((_, index) => (
                    <span key={`empty-${index}`} className="otp-digit">○</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              disabled={isLoading || code.length !== 6}
              className="verify-email-button"
            >
              {isLoading ? (
                <span className="button-content">
                  <svg className="animate-spin button-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </span>
              ) : (
                <span className="button-content">
                  <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Verify Email
                </span>
              )}
            </button>
          </form>

          {/* Message Display */}
          {message && (
            <div className={`verify-email-message ${message.includes('successfully') ? 'success' : 'error'}`}>
              <svg className="message-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {message.includes('successfully') ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                )}
              </svg>
              <span>{message}</span>
            </div>
          )}

          {/* Help Text */}
          <div className="verify-email-help">
            <p>Didn't receive the code?</p>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isResending || resendCooldown > 0 || !userEmail}
              className="resend-code-button"
            >
              {isResending ? (
                <span>
                  <svg className="animate-spin resend-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              ) : resendCooldown > 0 ? (
                `Resend code in ${resendCooldown}s`
              ) : (
                'Resend verification code'
              )}
            </button>
            <p className="help-text-small">Check your spam folder if you still don't see it</p>
          </div>

          {/* Navigation Links */}
          <div className="verify-email-links">
            <Link to="/login" className="verify-link">
              <svg className="link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Login
            </Link>
            <Link to="/signup" className="verify-link">
              Create Account
              <svg className="link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
